/**
 * Weather glyphs, drawn from primitives.
 *
 * The design draws these from discs and rounded bars on a 100-unit grid rather
 * than importing an icon set, so they belong to the same palette and typeface
 * as everything else and scale from 40px to 64px without going soft. Spec
 * §10.8 is explicit that the provider's stock PNGs will not survive next to
 * a considered type treatment at 1080p.
 *
 * Extending the set to twelve conditions meant staying inside that vocabulary
 * rather than reaching for paths — a snowflake is three small discs where the
 * rain drops were, and night is the sun with a bite taken out of it.
 */

export type GlyphKind =
  | "clear"
  | "few-clouds"
  | "scattered-clouds"
  | "overcast"
  | "light-rain"
  | "heavy-rain"
  | "thunderstorm"
  | "snow"
  | "sleet"
  | "fog"
  | "wind"
  | "unknown";

interface Part {
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** "c" for a circle, otherwise a corner radius in grid units. */
  r: number | "c";
  opacity?: number;
  rotate?: number;
}

const cloud = (dy: number, prefix: string): Part[] => [
  { key: `${prefix}a`, x: 6, y: 52 + dy, w: 82, h: 26, r: 13 },
  { key: `${prefix}b`, x: 14, y: 34 + dy, w: 34, h: 34, r: "c" },
  { key: `${prefix}c`, x: 40, y: 26 + dy, w: 44, h: 44, r: "c" },
];

const drops = (xs: number[], prefix: string): Part[] =>
  xs.map((x, i) => ({
    key: `${prefix}${String(i)}`,
    x,
    y: 74 + (i % 2) * 4,
    w: 8,
    h: 20,
    r: 4,
    opacity: 0.8,
  }));

const flakes = (xs: number[], prefix: string): Part[] =>
  xs.map((x, i) => ({
    key: `${prefix}${String(i)}`,
    x,
    y: 76 + (i % 2) * 6,
    w: 13,
    h: 13,
    r: "c" as const,
    opacity: 0.8,
  }));

function partsFor(kind: GlyphKind, night: boolean): Part[] {
  switch (kind) {
    case "clear":
      return night
        ? [
            { key: "moon", x: 20, y: 20, w: 60, h: 60, r: "c" },
            // The bite. Painted in the surface colour, which is what makes it
            // a crescent without needing a path.
            { key: "bite", x: 6, y: 12, w: 52, h: 52, r: "c", opacity: -1 },
          ]
        : [{ key: "sun", x: 20, y: 20, w: 60, h: 60, r: "c" }];

    case "few-clouds":
      return [
        { key: "sun", x: 46, y: 6, w: 42, h: 42, r: "c" },
        ...cloud(8, "f"),
      ];

    case "scattered-clouds":
      return [
        { key: "back", x: 30, y: 14, w: 40, h: 40, r: "c", opacity: 0.35 },
        ...cloud(8, "s"),
      ];

    case "overcast":
      return [
        ...cloud(-6, "b").map((p) => ({ ...p, key: `o${p.key}`, opacity: 0.4 })),
        ...cloud(8, "o"),
      ];

    case "light-rain":
      return [...cloud(-8, "r"), ...drops([26, 56], "d")];

    case "heavy-rain":
      return [...cloud(-10, "h"), ...drops([14, 32, 50, 68], "d")];

    case "thunderstorm":
      return [
        ...cloud(-10, "t"),
        { key: "bolt", x: 42, y: 68, w: 12, h: 30, r: 2, rotate: 18 },
      ];

    case "snow":
      return [...cloud(-8, "n"), ...flakes([22, 44, 66], "k")];

    case "sleet":
      // Alternating: one drop, one flake, one drop.
      return [
        ...cloud(-8, "l"),
        ...drops([22], "d"),
        ...flakes([44], "k"),
        ...drops([66], "e"),
      ];

    case "fog":
      return [
        { key: "f1", x: 10, y: 34, w: 80, h: 11, r: 6, opacity: 0.75 },
        { key: "f2", x: 18, y: 54, w: 66, h: 11, r: 6, opacity: 0.5 },
        { key: "f3", x: 10, y: 74, w: 78, h: 11, r: 6, opacity: 0.3 },
      ];

    case "wind":
      return [
        { key: "w1", x: 8, y: 38, w: 70, h: 10, r: 5 },
        { key: "w2", x: 68, y: 30, w: 10, h: 26, r: 5 },
        { key: "w3", x: 8, y: 62, w: 54, h: 10, r: 5, opacity: 0.7 },
        { key: "w4", x: 52, y: 56, w: 10, h: 22, r: 5, opacity: 0.7 },
      ];

    case "unknown":
      return cloud(4, "u");
  }
}

export function WeatherGlyph({
  kind,
  size,
  night = false,
  color = "var(--ink-soft)",
  surface = "#efe9da",
}: {
  kind: GlyphKind;
  size: number;
  night?: boolean;
  color?: string;
  /** Needed for the crescent's bite, which is painted rather than cut. */
  surface?: string;
}): React.JSX.Element {
  const unit = size / 100;

  return (
    <span
      className="glyph"
      style={{ width: size, height: size }}
      role="img"
      aria-label={kind.replace("-", " ")}
    >
      {partsFor(kind, night).map((part) => (
        <span
          key={part.key}
          style={{
            position: "absolute",
            left: part.x * unit,
            top: part.y * unit,
            width: part.w * unit,
            height: part.h * unit,
            borderRadius: part.r === "c" ? "50%" : part.r * unit,
            background: part.opacity === -1 ? surface : color,
            opacity: part.opacity === -1 ? 1 : part.opacity,
            transform: part.rotate === undefined ? undefined : `rotate(${String(part.rotate)}deg)`,
          }}
        />
      ))}
    </span>
  );
}
